import { useToast } from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ProductActivationService from "@/services/productActivationService";

import * as z from "zod";

export const useProductActivation = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [existingActivation, setExistingActivation] = useState(null);

  const createSchema = () => {
    const formFieldSchema = z.object({
      id: z.string().optional(),
      label: z.string().min(1, { message: t("common.required") }),
      type: z.string().min(1, { message: t("common.required") }),
      required: z.boolean().default(false),
      options: z.array(z.string()).optional(),
      order: z.number().optional(),
      brandColor: z.string().optional(),
      redirectUrl: z.string().optional(),
    });

    return z.object({
      brand: z.string().min(1, { message: t("common.required") }),
      subdomain: z.string().min(1, { message: t("common.required") }),
      warrantyFileOption: z.boolean(),
      billingFileOption: z.boolean(),
      billingWaiverOption: z.boolean(),
      serviceFileOption: z.boolean(),
      file: z.any().optional(),
      brandColor: z.string().optional(),
      redirectUrl: z.string().optional(),
      formFields: z.array(formFieldSchema).optional(),
    });
  };

  const form = useForm({
    resolver: zodResolver(createSchema()),
    defaultValues: {
      brand: "",
      subdomain: "",
      warrantyFileOption: false,
      billingFileOption: false,
      billingWaiverOption: false,
      serviceFileOption: false,
      file: null,
      brandColor: "",
      formFields: [],
      redirectUrl: "https://app.egaranti.com/login/jwt",
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "formFields",
  });

  // Load existing activation
  useEffect(() => {
    const loadActivation = async () => {
      try {
        setIsLoading(true);
        const response = await ProductActivationService.getProductActivations();

        let activation = null;
        if (Array.isArray(response) && response.length > 0) {
          activation = response[0];
        } else if (
          response &&
          typeof response === "object" &&
          !Array.isArray(response)
        ) {
          activation = response;
        }

        if (activation) {
          setExistingActivation(activation);

          let transformedFormFields = [];
          if (activation.questions && Array.isArray(activation.questions)) {
            transformedFormFields = activation.questions
              .map((q, index) => ({
                id: `field_${q.order !== undefined ? q.order : index}`,
                label: q.question || "",
                type: q.type ? q.type.toLowerCase() : "text",
                required: q.required || false,
                options: Array.isArray(q.options)
                  ? q.options.filter((opt) => opt !== "")
                  : [],
                order: q.order !== undefined ? q.order : index,
              }))
              .sort((a, b) => a.order - b.order);
          }

          form.reset({
            brand: activation.brand || "",
            subdomain: activation.subdomain || "",
            warrantyFileOption: activation.warrantyFileOption || false,
            billingFileOption: activation.billingFileOption || false,
            billingWaiverOption: activation.billingWaiverOption || false,
            serviceFileOption: activation.serviceFileOption || false,
            formFields: transformedFormFields,
            brandColor: activation.brandColor || "",
            redirectUrl: activation.redirectUrl || "",
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadActivation();
  }, [form, toast, t]);

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const addFormField = (type = "text") => {
    const currentFields = form.getValues("formFields") || [];
    const newOrder =
      (currentFields.length > 0
        ? Math.max(...currentFields.map((f) => f.order || 0))
        : 0) + 1;

    append({
      id: `field_${Date.now()}`,
      label: "",
      type,
      required: false,
      options: [],
      order: newOrder,
    });
  };

  const updateFieldOrder = (oldIndex, newIndex) => {
    const currentFields = form.getValues("formFields") || [];
    const updatedFields = [...currentFields];

    // Update orders
    updatedFields.forEach((field, index) => {
      field.order = index;
    });

    form.setValue("formFields", updatedFields);
    move(oldIndex, newIndex);
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      let base64Logo = null;

      if (data.file) {
        base64Logo = await convertFileToBase64(data.file);
      }

      // Transform formFields back to questions format with order
      const questions =
        data.formFields
          ?.map((field) => ({
            question: field.label,
            required: field.required,
            type: field.type.toUpperCase(),
            options: Array.isArray(field.options) ? field.options : [],
            order: field.order || 0,
          }))
          .sort((a, b) => a.order - b.order) || [];

      const payload = {
        brand: data.brand,
        subdomain: data.subdomain,
        brandColor: data.brandColor,
        warrantyFileOption: data.warrantyFileOption,
        billingFileOption: data.billingFileOption,
        // Fatura alanı kapalıyken ibraz onayının açık kalması anlamsız olurdu.
        billingWaiverOption: data.billingFileOption && data.billingWaiverOption,
        serviceFileOption: data.serviceFileOption,
        redirectUrl: data.redirectUrl,
        questions: questions,
      };

      if (base64Logo) {
        payload.logo = base64Logo;
      }

      if (existingActivation) {
        await ProductActivationService.updateActivation(payload);
      } else {
        await ProductActivationService.createActivation(payload);
      }

      toast({
        title: t("common.success"),
        variant: "success",
      });
    } catch (error) {
      toast({
        variant: "error",
        description:
          error.response?.data?.errors?.[0]?.message || error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    fields,
    isLoading,
    existingActivation,
    addFormField,
    remove,
    updateFieldOrder,
    onSubmit,
  };
};
